import { useTranslation } from 'react-i18next';
import { FormControl, FormItem, FormLabel } from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { UniversalMultiCombobox } from '@/components/common/UniversalMultiCombobox';

interface MatterTeamFieldsProps {
  selectedAttorney: number | undefined;
  setSelectedAttorney: (id: number | undefined) => void;
  selectedStaffs: number[];
  setSelectedStaffs: (ids: number[]) => void;
  selectedPartner: number | undefined;
  setSelectedPartner: (id: number | undefined) => void;
  isMdManaged: boolean;
  setIsMdManaged: (value: boolean) => void;
  hasAccess: (resource: string, action: string) => boolean;
}

export function MatterTeamFields({
  selectedAttorney,
  setSelectedAttorney,
  selectedStaffs,
  setSelectedStaffs,
  selectedPartner,
  setSelectedPartner,
  isMdManaged,
  setIsMdManaged,
  hasAccess,
}: MatterTeamFieldsProps) {
  const { t } = useTranslation();

  return (
    <>
      <Separator />

      <div className="space-y-4">
        <h1 className="text-xl font-semibold">{t('related')}</h1>
      </div>

      {/* MD-managed case — when checked, partner/lead/staff are all optional. */}
      <FormItem>
        <label className="flex items-start gap-2 cursor-pointer">
          <Checkbox
            checked={isMdManaged}
            onCheckedChange={(value) => setIsMdManaged(value === true)}
            className="mt-0.5"
          />
          <span className="flex flex-col">
            <span className="font-medium">{t('mdManagedCase')}</span>
            <span className="text-sm text-muted-foreground">
              {t('mdManagedCaseHint')}
            </span>
          </span>
        </label>
      </FormItem>

      {/* Partner — required unless the case is MD-managed */}
      <FormItem>
        <FormLabel className={isMdManaged ? undefined : 'required'}>
          {t('partners')}
        </FormLabel>
        <FormControl>
          <UniversalCombobox
            url="/users"
            listUrl="/users/select-box"
            labelKey="name"
            valueKey="id"
            selectedId={selectedPartner}
            params={{ status: 'ACTIVE', role_type: 'PARTNER' }}
            onSelectItem={(item) => {
              const partnerId = item?.['id'] as number | undefined;
              setSelectedPartner(partnerId);
              // The same user can't hold both the Partner and Lead slots.
              if (partnerId != null && partnerId === selectedAttorney) {
                setSelectedAttorney(undefined);
              }
            }}
            placeholder={t('selectPartner')}
            // disabled={!hasAccess('matter', 'add-partner')}
          />
        </FormControl>
      </FormItem>

      {/* Responsible Attorney (Lead) — optional at creation. Lead- and
          partner-type users are eligible (2026-07-14). */}
      <FormItem>
        <FormLabel>{t('responsibleAttorneys')}</FormLabel>
        <FormControl>
          <UniversalCombobox
            url="/users"
            listUrl="/users/select-box"
            labelKey="name"
            valueKey="id"
            selectedId={selectedAttorney}
            params={{ status: 'ACTIVE', role_types: 'LEAD,PARTNER' }}
            onSelectItem={(item) => {
              const leadId = item?.['id'] as number | undefined;
              setSelectedAttorney(leadId);
              // A lead can't also be staff on the same case — drop them from the
              // staff list if they were already picked there.
              if (leadId != null) {
                setSelectedStaffs(selectedStaffs.filter((id) => id !== leadId));
              }
              // ...and can't be the matter Partner at the same time.
              if (leadId != null && leadId === selectedPartner) {
                setSelectedPartner(undefined);
              }
            }}
            placeholder={t('selectAttorney')}
            disabled={!hasAccess('matter', 'add-attorney')}
          />
        </FormControl>
      </FormItem>

      {/* Responsible Staff — optional at creation. The pool is staff + leads
          (lead-type users may work as staff); the chosen case lead is hidden. */}
      <FormItem>
        <FormLabel>{t('responsibleStaff')}</FormLabel>
        <FormControl>
          <UniversalMultiCombobox
            url="/users"
            listUrl="/users/select-box"
            labelKey="name"
            valueKey="id"
            selectedIds={selectedStaffs}
            hiddenIds={selectedAttorney != null ? [selectedAttorney] : []}
            enableSelectAll
            params={{ status: 'ACTIVE', role_type: 'STAFF' }}
            onSelectItem={(items) => {
              setSelectedStaffs(items.map((item) => item.id as number));
            }}
            placeholder={t('selectStaff')}
            disabled={!hasAccess('matter', 'add-staff')}
          />
        </FormControl>
      </FormItem>
    </>
  );
}
